<template>
  <div>
    <Header />
    <v-container>
      <v-row class="mb-2" align="center">
        <v-col cols="12" sm="auto">
          <h2 class="title">
            <v-icon class="mr-2">mdi-needle</v-icon>
            Stickerei – Produktionsliste
            <v-chip class="ml-2" small>{{ skuGroups.length }} SKUs</v-chip>
          </h2>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="12" sm="auto">
          <v-btn small :color="hotfolderName ? 'success' : 'warning'" @click="selectHotfolder">
            <v-icon left small>mdi-folder-open</v-icon>
            {{ hotfolderName ? 'Hotfolder: ' + hotfolderName : 'Hotfolder wählen' }}
          </v-btn>
        </v-col>
        <v-col v-if="selectedSkus.length > 0" cols="12" sm="auto">
          <v-btn small color="grey" text @click="selectedSkus = []">Auswahl aufheben</v-btn>
        </v-col>
        <v-col v-if="selectedSkus.length > 0" cols="12" sm="auto">
          <v-btn small color="success" @click="downloadBatchCsv" :loading="batchDownloading">
            <v-icon left small>mdi-file-delimited</v-icon>
            {{ selectedSkus.length }} CSV(s) exportieren
          </v-btn>
        </v-col>
        <v-col cols="12" sm="auto">
          <v-btn small color="orange darken-2" dark @click="openDummyDialog">
            <v-icon left small>mdi-plus-circle-outline</v-icon>
            Dummy/Fix erstellen
          </v-btn>
        </v-col>
      </v-row>

      <!-- Dummy/Fix dialog -->
      <v-dialog v-model="dummyDialog.show" max-width="480" persistent>
      <v-card>
        <v-card-title class="headline">
          <v-icon class="mr-2" color="orange darken-2">mdi-plus-circle-outline</v-icon>
          Dummy/Fix-Eintrag erstellen
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="dummyDialog.orderId" label="Order ID *" outlined dense class="mb-2" :disabled="dummyDialog.loading" />
          <v-autocomplete v-model="dummyDialog.sku" :items="embroideryProducts" item-text="sku" item-value="sku" label="SKU *" outlined dense class="mb-2" :disabled="dummyDialog.loading" :loading="productsLoading" clearable />
          <v-text-field v-model="dummyDialog.line1" label="Text (line1)" outlined dense class="mb-2" :disabled="dummyDialog.loading" />
          <v-select v-model="dummyDialog.line2" :items="colorOptions" label="Farbe (line2)" outlined dense class="mb-2" :disabled="dummyDialog.loading" clearable />
          <v-select v-model="dummyDialog.line3" :items="fontOptions"  label="Font (line3)"  outlined dense :disabled="dummyDialog.loading" clearable />
          <div v-if="dummyDialog.error" class="mt-2 error--text text-caption">{{ dummyDialog.error }}</div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="dummyDialog.loading" @click="closeDummyDialog">Abbrechen</v-btn>
          <v-btn color="orange darken-2" dark :loading="dummyDialog.loading" @click="submitDummy">
            <v-icon left small>mdi-printer</v-icon>
            Erstellen & drucken
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

      <v-data-table
        :headers="skuHeaders"
        :items="skuGroups"
        :loading="loading"
        dense
        class="elevation-1"
        no-data-text="Keine offenen Aufträge."
        hide-default-footer
        :items-per-page="-1"
      >
        <!-- Select-all header checkbox -->
        <template #[`header.selected`]>
          <v-simple-checkbox
            :value="allSelected"
            :indeterminate="someSelected"
            @input="toggleSelectAll"
            color="primary"
          ></v-simple-checkbox>
        </template>

        <!-- Row checkbox -->
        <template #[`item.selected`]="{ item }">
          <v-simple-checkbox
            :value="selectedSkus.includes(item.sku)"
            @input="toggleSkuSelection(item.sku)"
            color="primary"
          ></v-simple-checkbox>
        </template>

        <!-- SKU with thumbnail -->
        <template #[`item.sku`]="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="36" tile class="mr-3 grey lighten-3">
              <v-img :src="skuImageUrl(item.sku)" contain>
                <template v-slot:placeholder>
                  <v-icon color="grey lighten-1">mdi-tshirt-crew</v-icon>
                </template>
              </v-img>
            </v-avatar>
            <span style="font-weight: 600; font-size: 13px;">{{ item.sku }}</span>
          </div>
        </template>

        <!-- Gescannt count -->
        <template #[`item.scanned`]="{ item }">
          <v-chip x-small :color="item.scanned > 0 ? 'success' : 'grey lighten-2'" :dark="item.scanned > 0">
            {{ item.scanned }} gescannt
          </v-chip>
        </template>
      </v-data-table>
    </v-container>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top :timeout="4000">
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import Header from "../header";
import productionService from "@/services/ProductionService";

const HOTFOLDER_KEY = 'embroidery_hotfolder_name';
const AUTO_RELOAD_INTERVAL = 30000;

export default {
  name: "EmbroideryV1",
  components: { Header },
  data: () => ({
    items: [],
    loading: false,
    hotfolderHandle: null,
    hotfolderName: '',
    _reloadTimer: null,
    selectedSkus: [],
    batchDownloading: false,
    skuHeaders: [
      { text: '',         value: 'selected', sortable: false, width: 40 },
      { text: 'SKU',      value: 'sku' },
      { text: 'Gescannt', value: 'scanned',  sortable: false, width: 140 },
    ],
    snackbar: { show: false, text: "", color: "success" },
    colorOptions: ['Dunkelblau', 'Dunkelrot', 'Dunkelbraun', 'Creme'],
    fontOptions: ['Amatic', 'Londrina', 'Garamond'],
    embroideryProducts: [],
    productsLoading: false,
    dummyDialog: {
      show: false,
      loading: false,
      error: '',
      orderId: '',
      sku: '',
      line1: '',
      line2: '',
      line3: '',
    },
  }),

  computed: {
    skuGroups() {
      const map = {};
      for (const item of this.items) {
        const sku = item.sku || "(kein SKU)";
        if (!map[sku]) map[sku] = { sku, count: 0, scanned: 0, exported: 0 };
        map[sku].count++;
        if (item.status === 'scanned') map[sku].scanned++;
        if (item.status === 'exported') map[sku].exported++;
      }
      return Object.values(map)
        .filter(g => g.scanned > 0 || g.count - g.scanned - g.exported > 0)
        .sort((a, b) => b.scanned - a.scanned || b.count - a.count);
    },

    allSelected() {
      return this.skuGroups.length > 0 && this.selectedSkus.length === this.skuGroups.length;
    },

    someSelected() {
      return this.selectedSkus.length > 0 && this.selectedSkus.length < this.skuGroups.length;
    },
  },

  async mounted() {
    this.loading = true;
    await this.fetchEntries();
    this.loading = false;
    this.hotfolderName = localStorage.getItem(HOTFOLDER_KEY) || '';
    this._reloadTimer = setInterval(() => this.fetchEntries(), AUTO_RELOAD_INTERVAL);
  },

  beforeDestroy() {
    clearInterval(this._reloadTimer);
  },

  methods: {
    async fetchEntries() {
      try {
        const response = await productionService.getEmbroideryProductionList();
        this.items = response.data;
      } catch (error) {
        console.error("Error fetching embroidery production list:", error);
      }
    },

    skuImageUrl(sku) {
      return `https://backoffice.accelery.de/api/files/sku?sku=${encodeURIComponent(sku)}`;
    },

    async selectHotfolder() {
      if (!window.showDirectoryPicker) {
        this.showSnackbar('Browser unterstützt kein Ordner-Picker (Chrome empfohlen)', 'error');
        return;
      }
      try {
        const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
        this.hotfolderHandle = handle;
        this.hotfolderName = handle.name;
        localStorage.setItem(HOTFOLDER_KEY, handle.name);
        this.showSnackbar(`Hotfolder gesetzt: ${handle.name}`, 'success');
      } catch (err) {
        if (err.name !== 'AbortError') this.showSnackbar('Fehler beim Ordner wählen', 'error');
      }
    },

    toggleSelectAll(val) {
      this.selectedSkus = val ? this.skuGroups.map(g => g.sku) : [];
    },

    toggleSkuSelection(sku) {
      const idx = this.selectedSkus.indexOf(sku);
      if (idx === -1) this.selectedSkus.push(sku);
      else this.selectedSkus.splice(idx, 1);
    },

    async downloadBatchCsv() {
      if (this.selectedSkus.length === 0) return;
      this.batchDownloading = true;
      let successCount = 0;
      let failCount = 0;

      for (const sku of this.selectedSkus) {
        try {
          await this.exportCsvForSku(sku);
          successCount++;
        } catch {
          failCount++;
        }
      }

      this.selectedSkus = [];
      this.batchDownloading = false;

      if (failCount === 0) {
        this.showSnackbar(`${successCount} CSV(s) exportiert`, 'success');
      } else {
        this.showSnackbar(`${successCount} ok, ${failCount} fehlgeschlagen`, 'warning');
      }
      await this.fetchEntries();
    },

    async exportCsvForSku(sku) {
      if (!this.hotfolderHandle && this.hotfolderName && window.showDirectoryPicker) {
        try {
          this.hotfolderHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
          this.hotfolderName = this.hotfolderHandle.name;
          localStorage.setItem(HOTFOLDER_KEY, this.hotfolderHandle.name);
        } catch (err) {
          if (err.name !== 'AbortError') console.warn('Could not re-acquire hotfolder handle:', err);
        }
      }

      const response = await productionService.downloadEmbroideryCsv(sku);
      const csvBlob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const time = new Date(Date.now() + 3600000).toISOString().slice(11, 19).replace(/:/g, '_');
      const safeSku = sku.replace(/[^a-zA-Z0-9_-]/g, '_');
      const fileName = `${safeSku}_${time}.csv`;

      if (this.hotfolderHandle) {
        try {
          const fileHandle = await this.hotfolderHandle.getFileHandle(fileName, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(csvBlob);
          await writable.close();
          return;
        } catch (err) {
          console.warn('Hotfolder write failed, falling back to download:', err);
          this.hotfolderHandle = null;
          this.showSnackbar('Hotfolder-Zugriff verloren – CSV wird heruntergeladen.', 'warning');
        }
      }

      const url = window.URL.createObjectURL(csvBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      window.URL.revokeObjectURL(url);
    },

    showSnackbar(text, color = "success") {
      this.snackbar = { show: true, text, color };
    },

    async openDummyDialog() {
      this.dummyDialog = { show: true, loading: false, error: '', orderId: '', sku: '', line1: '', line2: '', line3: '' };
      if (this.embroideryProducts.length === 0) {
        this.productsLoading = true;
        try {
          const res = await productionService.getEmbroideryProducts();
          this.embroideryProducts = res.data || [];
        } catch { /* ignore */ } finally {
          this.productsLoading = false;
        }
      }
    },

    closeDummyDialog() {
      this.dummyDialog.show = false;
    },

    async submitDummy() {
      if (!this.dummyDialog.orderId || !this.dummyDialog.sku) {
        this.dummyDialog.error = 'Order ID und SKU sind Pflichtfelder.';
        return;
      }
      this.dummyDialog.loading = true;
      this.dummyDialog.error = '';
      try {
        const res = await productionService.createEmbroideryDummy({
          orderId: this.dummyDialog.orderId,
          sku: this.dummyDialog.sku,
          line1: this.dummyDialog.line1 || null,
          line2: this.dummyDialog.line2 || null,
          line3: this.dummyDialog.line3 || null,
        });
        const msg = res.data.labelPrinted
          ? `Eintrag erstellt & Label gedruckt (UUID: ${res.data.uuid})`
          : `Eintrag erstellt – Label-Druck fehlgeschlagen: ${res.data.warning || ''}`;
        this.closeDummyDialog();
        this.showSnackbar(msg, res.data.labelPrinted ? 'success' : 'warning');
        await this.fetchEntries();
      } catch (err) {
        this.dummyDialog.error = err.response?.data?.error || 'Fehler beim Erstellen.';
      } finally {
        this.dummyDialog.loading = false;
      }
    },
  },
};
</script>
